// src/windowLock.js
//
// On a computer the window itself never scrolls: body is overflow:hidden
// (index.css) and every pane scrolls inside itself. overflow:hidden stops a
// wheel, not code: a focus() or scrollIntoView on something past the edge
// still scrolls the window, or an app frame that is overflow:hidden, and then
// nothing can scroll it back. The whole app slid up and left an empty band
// under it until a reload (the founder, 2026-11-01: the model menu focused
// its row a beat before it took its place; a toast parked 100px under the
// frame gave the frame hidden room to slide into). So on a computer the
// window, and every frame marked data-no-scroll while it is overflow hidden,
// goes straight back to the top. A phone (768px and under) scrolls the
// window on purpose (index.css), and is left alone.
export const DESKTOP = '(min-width: 769px)';

/** Keeps the window and the app's frames at the top on a computer; returns the undo. */
export function lockWindow(win = typeof window !== 'undefined' ? window : null) {
  if (!win || typeof win.addEventListener !== 'function') return () => {};
  const desk = () => {
    try { return win.matchMedia ? win.matchMedia(DESKTOP).matches : true; } catch { return true; }
  };
  const onScroll = () => {
    if ((win.scrollY || win.scrollX) && desk()) win.scrollTo(0, 0);
  };
  // A frame's own scroll: scroll events do not bubble, so this listens in
  // the capture phase. Only a marked frame that is not meant to scroll (its
  // overflow hidden now; an immersive page's frame scrolls on a phone).
  const onFrameScroll = (e) => {
    const el = e.target;
    if (!el || el.nodeType !== 1 || !el.hasAttribute('data-no-scroll')) return;
    if (!(el.scrollTop || el.scrollLeft) || !desk()) return;
    const style = win.getComputedStyle ? win.getComputedStyle(el) : null;
    const fixedOn = (axis) => !style || /hidden|clip/.test(style[axis] || style.overflow || '');
    if (el.scrollTop && fixedOn('overflowY')) el.scrollTop = 0;
    if (el.scrollLeft && fixedOn('overflowX')) el.scrollLeft = 0;
  };
  const doc = win.document;
  win.addEventListener('scroll', onScroll, { passive: true });
  doc?.addEventListener?.('scroll', onFrameScroll, { capture: true, passive: true });
  return () => {
    win.removeEventListener('scroll', onScroll);
    doc?.removeEventListener?.('scroll', onFrameScroll, { capture: true });
  };
}
